import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  LinearProgress, Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { Close, DoNotDisturbAltOutlined, LockOpenOutlined, LockOutlined, Save, Send } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { SheetScope } from '../../../services/examService';
import { SheetStudent } from '../../../types/examination';
import { SubjectStatus } from '../../../types';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import ReasonDialog from '../../common/ReasonDialog';
import EmptyState from '../../common/EmptyState';
import LastEdited, { LastEditedInfo } from '../LastEdited';
import PaperHistoryDialog, { HistoryTarget } from '../marks/PaperHistoryDialog';
import { PaperDraft } from '../marks/paperStatus';
import MarksSheetRow from './MarksSheetRow';
import SubmitMarksDialog from './SubmitMarksDialog';
import { useMarksSheet } from './useMarksSheet';

/** A sheet as the Enter Marks list opens it. */
export interface MarksSheetTarget extends SheetScope {
  examName: string;
  className: string;
  subjectName: string;
  /** e.g. "Section A" or "Whole class". */
  sectionLabel: string;
  lastEdited?: LastEditedInfo | null;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Enter one subject's marks for a section or the whole class. Typing a mark
 * marks the student present; Enter or ↓ moves on, ↑ goes back, "A" marks
 * absent, "N" not appeared. Students who left or joined late are flagged so
 * nobody wonders why they have no mark. Submitting locks the papers; only
 * someone allowed to unlock can reopen them, and says why.
 */
const MarksEntryDialog: React.FC<{ target: MarksSheetTarget | null; onClose: () => void; onSaved: () => void }> = ({ target, onClose, onSaved }) => {
  const { canWrite } = useModulePermissions();
  const readOnly = !canWrite('enter_marks');
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const scope = useMemo<SheetScope | null>(
    () => (target ? { examId: target.examId, classId: target.classId, sectionInstanceId: target.sectionInstanceId, subjectId: target.subjectId } : null),
    [target],
  );
  const { sheet, drafts, loading, error, refused, changed, problems, tally, setDraft, markRestNotAppeared, save, unlock } = useMarksSheet(scope);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [history, setHistory] = useState<HistoryTarget | null>(null);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const students = useMemo(() => sheet?.students ?? [], [sheet]);
  const open = students.length - tally.locked;
  const allLocked = students.length > 0 && open === 0;
  const dirty = changed.length > 0;
  const canUnlock = canWrite('unlock_marks') && tally.locked > 0;
  const changedProblems = problems.filter(i => changed.includes(i)).length;

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const focusRow = useCallback((from: number, step: 1 | -1) => {
    for (let k = from; k >= 0 && k < inputs.current.length; k += step) {
      const el = inputs.current[k];
      if (el && !el.disabled) { el.focus(); return; }
    }
  }, []);

  const registerInput = useCallback((i: number, el: HTMLInputElement | null) => { inputs.current[i] = el; }, []);

  const onKeyDown = useCallback((i: number, e: React.KeyboardEvent) => {
    const key = e.key.toLowerCase();
    if (key === 'enter' || key === 'arrowdown') { e.preventDefault(); focusRow(i + 1, 1); }
    else if (key === 'arrowup') { e.preventDefault(); focusRow(i - 1, -1); }
    else if (key === 'a' || key === 'n') {
      e.preventDefault();
      setDraft(i, { status: key === 'a' ? SubjectStatus.ABSENT : SubjectStatus.NOT_APPEARED, marks: '' });
      focusRow(i + 1, 1);
    }
  }, [focusRow, setDraft]);

  const onChange = useCallback((i: number, next: PaperDraft) => setDraft(i, next), [setDraft]);
  const onHistory = useCallback((s: SheetStudent) => setHistory({ resultId: s.resultId, subjectId: target!.subjectId, title: `${s.name} · ${target!.subjectName}` }), [target]);

  const run = async (lock: boolean) => {
    setSaving(true);
    try {
      const outcome = await save(lock);
      if (!outcome) return;
      onSaved();
      if (outcome.refused.length) {
        const changed = outcome.refused.filter(r => r.code === 'changed').length;
        enqueueSnackbar(changed
          ? `${plural(outcome.saved, 'paper')} saved. ${plural(changed, 'paper was', 'papers were')} changed by someone else while you had the sheet open — check the highlighted rows.`
          : `${plural(outcome.saved, 'paper')} saved. ${plural(outcome.refused.length, 'paper')} couldn't be — see the highlighted rows.`, { variant: 'warning' });
        return;
      }
      enqueueSnackbar(lock ? `${target!.subjectName} submitted — ${plural(outcome.saved, 'paper')} locked` : `${target!.subjectName} saved — ${plural(outcome.saved, 'change')}`, { variant: 'success' });
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not save the marks', { variant: 'error' });
    } finally {
      setSaving(false);
      setConfirmSubmit(false);
    }
  };

  const fillRest = () => {
    const n = markRestNotAppeared();
    enqueueSnackbar(`${plural(n, 'student')} marked not appeared. Save to keep it.`, { variant: 'info' });
  };

  const lockedBy = useMemo(() => {
    const p = students.find(s => s.paper.isLocked)?.paper;
    return p?.lockedBy ? `by ${p.lockedBy}${p.lockedAt ? ` on ${format(new Date(p.lockedAt), 'd MMM yyyy')}` : ''}` : '';
  }, [students]);

  const submitBlock = tally.pending > 0
    ? `${plural(tally.pending, 'student')} still need a mark, Absent or Not appeared`
    : problems.length ? 'Fix the highlighted marks first' : '';

  return (
    <>
      <Dialog open={!!target} onClose={requestClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: { md: 'min(860px, 92vh)' } } }}>
        <DialogTitle component="div" sx={{ pb: 1.5 }}>
          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>{target?.subjectName} · {target?.examName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {target?.className} · {target?.sectionLabel}{sheet ? ` · out of ${sheet.maxMarks}` : ''}
              </Typography>
              <LastEdited value={target?.lastEdited} />
            </Box>
            {canUnlock && (
              <Button size="small" color="inherit" startIcon={<LockOpenOutlined fontSize="small" />} onClick={() => setUnlocking(true)} sx={{ color: 'text.secondary' }}>
                Unlock
              </Button>
            )}
            <IconButton onClick={requestClose} size="small" aria-label="Close"><Close fontSize="small" /></IconButton>
          </Stack>
          {students.length > 0 && (
            <Box sx={{ mt: 1.75 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }} spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  {students.length - tally.pending} of {students.length} marked
                  {tally.average !== null ? ` · average ${tally.average.toFixed(1)}%` : ''}
                  {tally.absent ? ` · ${tally.absent} absent` : ''}
                  {tally.notAppeared ? ` · ${tally.notAppeared} not appeared` : ''}
                </Typography>
                {!allLocked && !readOnly && <Typography variant="caption" color="text.disabled" sx={{ display: { xs: 'none', sm: 'block' } }}>Enter ↓ next · ↑ back · A absent · N not appeared</Typography>}
              </Stack>
              <LinearProgress
                variant="determinate"
                value={((students.length - tally.pending) / students.length) * 100}
                sx={{ height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
              />
            </Box>
          )}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {loading && !sheet ? (
            <Box sx={{ p: 4 }}><LinearProgress /></Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
          ) : students.length === 0 ? (
            <EmptyState
              dense
              icon={<DoNotDisturbAltOutlined />}
              title="Nobody sits this paper here"
              description="Students get this paper when the exam is published, or when they join the class later. Check the class's enrollments and the subject's curriculum."
              sx={{ m: 3 }}
            />
          ) : (
            <Box>
              {tally.locked > 0 && (
                <Alert severity={allLocked ? 'success' : 'info'} icon={<LockOutlined fontSize="small" />} sx={{ m: 2, mb: 0 }}>
                  {allLocked
                    ? `${['Submitted', lockedBy].filter(Boolean).join(' ')}. These marks are locked${canUnlock ? ' — unlock them to make a correction.' : '; ask someone who can unlock marks to reopen them.'}`
                    : `${plural(tally.locked, 'paper')} submitted and locked. The rest are still open.`}
                </Alert>
              )}
              {!allLocked && !readOnly && tally.pending > 0 && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mx: 2, mt: 2, px: 1.5, py: 1, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
                  <Typography variant="body2" color="text.secondary">{plural(tally.pending, 'student')} without a mark yet.</Typography>
                  <Tooltip title="For students who weren't expected to sit this paper — they're left out of the overall %">
                    <Button size="small" onClick={fillRest}>Mark the rest not appeared</Button>
                  </Tooltip>
                </Stack>
              )}
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mt: 1, '& td, & th': { px: 2, py: 1, borderBottom: 1, borderColor: 'divider' } }}>
                <Box component="thead" sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                  <tr>
                    {['Roll', 'Student', 'Marks', ''].map((h, k) => (
                      <Box component="th" key={k} sx={{ textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>{h}</Box>
                    ))}
                  </tr>
                </Box>
                <tbody>
                  {students.map((st, i) => {
                    const newSection = !target?.sectionInstanceId && st.sectionName && st.sectionName !== students[i - 1]?.sectionName;
                    return (
                      <React.Fragment key={st.studentId}>
                        {newSection && (
                          <tr>
                            <Box component="td" colSpan={4} sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025), py: '6px !important', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
                              Section {st.sectionName}
                            </Box>
                          </tr>
                        )}
                        <MarksSheetRow
                          index={i}
                          student={st}
                          draft={drafts[i]}
                          maxMarks={sheet!.maxMarks}
                          readOnly={readOnly}
                          refused={refused[st.studentId]}
                          onChange={onChange}
                          registerInput={registerInput}
                          onKeyDown={onKeyDown}
                          onHistory={onHistory}
                        />
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          {allLocked || readOnly || students.length === 0 ? (
            <Button variant="contained" disableElevation onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button color="inherit" onClick={requestClose} disabled={saving}>Cancel</Button>
              <Tooltip title={changedProblems ? 'Fix the highlighted marks first' : ''}>
                <span>
                  <Button variant="outlined" startIcon={<Save />} onClick={() => run(false)} disabled={saving || !dirty || changedProblems > 0}>
                    Save draft
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={submitBlock}>
                <span>
                  <Button variant="contained" disableElevation startIcon={<Send />} onClick={() => setConfirmSubmit(true)} disabled={saving || !!submitBlock}>
                    Submit &amp; lock
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </DialogActions>
      </Dialog>

      <SubmitMarksDialog
        open={confirmSubmit}
        subject={target?.subjectName ?? ''}
        tally={tally}
        papers={open}
        loading={saving}
        onClose={() => setConfirmSubmit(false)}
        onConfirm={() => run(true)}
      />

      <ReasonDialog
        open={unlocking}
        onClose={() => setUnlocking(false)}
        title={`Unlock ${target?.subjectName ?? ''} marks?`}
        facts={[
          { label: 'Sheet', value: `${target?.examName} · ${target?.className} · ${target?.sectionLabel}` },
          { label: 'Submitted papers', value: tally.locked },
        ]}
        consequence="The marks become editable again. Each paper's history keeps who unlocked it and why."
        confirmText="Unlock"
        confirmColor="warning"
        reasonPlaceholder="What needs correcting?"
        onConfirm={async reason => {
          await unlock(reason);
          onSaved();
          enqueueSnackbar(`${target?.subjectName} unlocked`, { variant: 'success' });
        }}
      />

      <ConfirmationDialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        title="Discard these marks?"
        message="The marks you typed haven't been saved. Save a draft to keep them, or discard to close."
        confirmText="Discard"
        cancelText="Keep editing"
      />

      <PaperHistoryDialog target={history} onClose={() => setHistory(null)} />
    </>
  );
};

export default MarksEntryDialog;
